import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEOHead';
import { 
  Sparkles, Brain, Image, Video, FileText, Shield, 
  Eye, Zap, CheckCircle, AlertTriangle, Bot, Camera,
  MessageSquare, Search, Home, TrendingUp
} from 'lucide-react';

const AIServicesPage = () => {
  const seoConfig = {
    title: 'Servizi AI | Intelligenza Artificiale per il Real Estate | WEQASA',
    description: 'Scopri come WEQASA utilizza l\'intelligenza artificiale per valutazioni immobiliari, generazione contenuti, analisi documenti e sicurezza avanzata.',
    keywords: 'intelligenza artificiale immobiliare, AI real estate, valutazione automatica immobili, analisi documenti AI'
  };

  const aiServices = [
    {
      icon: Home,
      title: 'Valutazione Immobiliare AI',
      description: 'Il nostro algoritmo analizza centinaia di parametri per fornire una stima accurata del valore di mercato del tuo immobile.',
      features: [
        'Analisi comparativa di mercato automatizzata',
        'Valutazione basata su dati storici delle vendite',
        'Aggiornamento in tempo reale dei valori',
        'Precisione media del 95%'
      ],
      badge: 'Core Feature'
    },
    {
      icon: Image,
      title: 'Generazione Immagini',
      description: 'Creiamo rendering fotorealistici e virtual staging per presentare al meglio gli immobili.',
      features: [
        'Virtual staging di ambienti vuoti',
        'Rendering di progetti di ristrutturazione',
        'Miglioramento qualità foto esistenti',
        'Rimozione oggetti indesiderati'
      ],
      badge: 'Premium'
    },
    {
      icon: Video,
      title: 'Video Tour Automatici',
      description: 'Generazione automatica di video tour professionali a partire dalle foto dell\'immobile.',
      features: [
        'Video tour con transizioni fluide',
        'Voiceover automatico personalizzabile',
        'Musica di sottofondo royalty-free',
        'Ottimizzazione per social media'
      ],
      badge: 'Premium'
    },
    {
      icon: FileText,
      title: 'Analisi Documenti',
      description: 'Estrazione automatica di informazioni da visure, planimetrie e documenti catastali.',
      features: [
        'OCR avanzato per documenti scannerizzati',
        'Estrazione dati catastali automatica',
        'Verifica conformità documentale',
        'Segnalazione anomalie e incongruenze'
      ],
      badge: 'Core Feature'
    },
    {
      icon: MessageSquare,
      title: 'Assistente Virtuale',
      description: 'Chatbot intelligente per rispondere alle domande di acquirenti e venditori 24/7.',
      features: [
        'Risposte immediate alle FAQ',
        'Prenotazione visite automatica',
        'Qualificazione lead intelligente',
        'Escalation a operatori umani'
      ],
      badge: 'Attivo'
    },
    {
      icon: Shield,
      title: 'Sicurezza AI',
      description: 'Sistemi avanzati per rilevare frodi, comportamenti sospetti e proteggere le transazioni.',
      features: [
        'Rilevamento tentativi di frode',
        'Analisi comportamentale utenti',
        'Verifica autenticità documenti',
        'Monitoraggio transazioni sospette'
      ],
      badge: 'Sempre Attivo'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F5F7] to-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0071e3]/10 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-[#0071e3]" />
            <span className="text-sm font-medium text-[#0071e3]">Powered by AI</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1D1D1F] tracking-tight mb-6">
            Intelligenza Artificiale
            <span className="block text-[#0071e3]">al servizio del Real Estate</span>
          </h1>
          <p className="text-xl text-[#86868B] max-w-3xl mx-auto leading-relaxed">
            WEQASA integra le più avanzate tecnologie di AI per rendere la compravendita 
            immobiliare più veloce, sicura e trasparente.
          </p>
        </div>
      </section>

      {/* AI Services Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-4">
              I Nostri Servizi AI
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Tecnologie all'avanguardia per ogni fase della compravendita immobiliare
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiServices.map((service, index) => (
              <div 
                key={index}
                className="bg-[#F5F5F7] rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-[#0071e3]" />
                  </div>
                  <span className="px-3 py-1 bg-[#0071e3] text-white text-xs font-medium rounded-full">
                    {service.badge}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#86868B] mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#424245]">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-[#F5F5F7]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-4">
              Come Funziona la Nostra AI
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Tecnologie trasparenti e sicure per proteggere i tuoi dati
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#0071e3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">Modelli Proprietari</h3>
              <p className="text-[#86868B]">
                I nostri modelli AI sono addestrati specificamente sul mercato immobiliare italiano, 
                garantendo risultati accurati e pertinenti.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#0071e3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">Privacy by Design</h3>
              <p className="text-[#86868B]">
                I tuoi dati sono elaborati in modo sicuro. Non vendiamo né condividiamo 
                informazioni personali con terze parti per addestrare modelli AI.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#0071e3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">Supervisione Umana</h3>
              <p className="text-[#86868B]">
                Ogni decisione importante è verificata da professionisti qualificati. 
                L'AI supporta, non sostituisce, il giudizio umano.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Partners */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-4">
              Tecnologie Utilizzate
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              Collaboriamo con i leader mondiali dell'intelligenza artificiale
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F5F5F7] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-4">Generazione Contenuti</h3>
              <ul className="space-y-3 text-[#424245]">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold">GPT</span>
                  </div>
                  <span>OpenAI GPT-4 per analisi testi e descrizioni</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-sm font-bold">G</span>
                  </div>
                  <span>Google Gemini per elaborazione multimodale</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Camera className="w-4 h-4" />
                  </div>
                  <span>Modelli proprietari per virtual staging</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#F5F5F7] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-4">Analisi e Sicurezza</h3>
              <ul className="space-y-3 text-[#424245]">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Search className="w-4 h-4" />
                  </div>
                  <span>Computer Vision per analisi immagini e documenti</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <span>Machine Learning per valutazioni predittive</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4" />
                  </div>
                  <span>Anomaly Detection per sicurezza transazioni</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 lg:py-28 bg-[#1D1D1F] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              Il Nostro Impegno per la Trasparenza
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Crediamo in un uso responsabile dell'intelligenza artificiale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 rounded-xl p-6">
              <AlertTriangle className="w-8 h-8 text-[#0071e3] mb-4" />
              <h3 className="font-semibold mb-2">Segnalazione AI</h3>
              <p className="text-sm text-gray-400">
                Tutti i contenuti generati dall'AI sono chiaramente identificati
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <Eye className="w-8 h-8 text-[#0071e3] mb-4" />
              <h3 className="font-semibold mb-2">Audit Regolari</h3>
              <p className="text-sm text-gray-400">
                I nostri sistemi sono sottoposti a verifiche periodiche per bias e accuratezza
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <Bot className="w-8 h-8 text-[#0071e3] mb-4" />
              <h3 className="font-semibold mb-2">Opt-Out</h3>
              <p className="text-sm text-gray-400">
                Puoi richiedere che i tuoi dati non siano usati per addestrare modelli
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <FileText className="w-8 h-8 text-[#0071e3] mb-4" />
              <h3 className="font-semibold mb-2">Conformità</h3>
              <p className="text-sm text-gray-400">
                Rispettiamo l'AI Act europeo e le normative sulla protezione dei dati
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-6">
            Pronto a scoprire il futuro del Real Estate?
          </h2>
          <p className="text-xl text-[#86868B] mb-10 max-w-2xl mx-auto">
            Registrati gratuitamente e prova le nostre tecnologie AI per vendere 
            o acquistare casa al miglior prezzo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/registrati/proprietario"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#0071e3] text-white font-medium rounded-full hover:bg-[#0077ED] transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Vendi con AI
            </Link>
            <Link 
              to="/registrati/acquirente"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F5F5F7] text-[#1D1D1F] font-medium rounded-full hover:bg-[#E8E8ED] transition-colors"
            >
              <Search className="w-5 h-5 mr-2" />
              Cerca Immobili
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-12 text-center">
            Domande Frequenti sull'AI
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6">
              <h3 className="font-semibold text-[#1D1D1F] mb-2">
                I miei dati vengono usati per addestrare l'AI?
              </h3>
              <p className="text-[#424245]">
                No, i dati personali degli utenti non vengono mai utilizzati per addestrare modelli AI 
                senza esplicito consenso. I nostri modelli sono addestrati su dataset pubblici e anonimizzati.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h3 className="font-semibold text-[#1D1D1F] mb-2">
                Le valutazioni AI sono affidabili?
              </h3>
              <p className="text-[#424245]">
                Le nostre valutazioni hanno una precisione media del 95% rispetto ai prezzi di vendita reali. 
                Tuttavia, consigliamo sempre di consultare anche un professionista per valutazioni definitive.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h3 className="font-semibold text-[#1D1D1F] mb-2">
                Posso disattivare le funzionalità AI?
              </h3>
              <p className="text-[#424245]">
                Alcune funzionalità core (come la sicurezza) utilizzano sempre l'AI. Per altre funzionalità 
                opzionali (come virtual staging), puoi scegliere se utilizzarle o meno nelle impostazioni del profilo.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h3 className="font-semibold text-[#1D1D1F] mb-2">
                Chi è responsabile delle decisioni prese dall'AI?
              </h3>
              <p className="text-[#424245]">
                WEQASA mantiene sempre la supervisione umana sulle decisioni importanti. L'AI fornisce 
                supporto e suggerimenti, ma le decisioni finali (es. approvazione di un'offerta) 
                coinvolgono sempre operatori qualificati.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIServicesPage;
